import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberLevelDto, TimedFeeTable } from '@pokerroom/types';
import { api } from '../../lib/api';

export function TimedFeeEditor({
  value,
  principal,
  legacyRake,
  onChange,
}: {
  value: TimedFeeTable | undefined;
  principal: string;
  legacyRake: string;
  onChange(value: TimedFeeTable): void;
}) {
  const { t } = useTranslation('tournament');
  const { data: levels = [], isError } = useQuery({
    queryKey: ['timed-fee-member-levels'],
    queryFn: () =>
      api.get<Pick<MemberLevelDto, 'id' | 'displayName'>[]>(
        '/tournament/templates/fee-member-levels',
      ),
  });
  const draft: TimedFeeTable = value ?? {
    generalMemberLevelId: '',
    principalAmount: principal,
    originalVenueFeeAmount: '',
    generalReservationVenueFeeAmount: '',
    memberVenuePrices: [],
    repeatVenueFees: { reentry: '', rebuy: '', addon: '' },
    afterBreakVenueFeeAmount: '',
  };
  const field = (
    key: 'originalVenueFeeAmount' | 'generalReservationVenueFeeAmount' | 'afterBreakVenueFeeAmount',
    label: string,
  ) => (
    <label className="flex flex-col gap-1 min-w-0">
      <span>{label}</span>
      <input
        className="w-full border border-base-300 bg-base-100 px-3 py-2 text-base-content focus:outline-2 focus:outline-primary disabled:opacity-60"
        type="text"
        inputMode="decimal"
        value={draft[key]}
        onChange={(event) =>
          onChange({ ...draft, principalAmount: principal, [key]: event.target.value })
        }
      />
    </label>
  );
  return (
    <fieldset className="border border-base-300 rounded-lg p-4 space-y-3">
      <legend className="font-semibold">{t('timedFees.title')}</legend>
      {!value && <p>{t('timedFees.legacy', { rate: legacyRake })}</p>}
      <p>{t('timedFees.explanation')}</p>
      {isError && <p role="alert">{t('timedFees.levelsUnavailable')}</p>}
      <label className="flex flex-col gap-1 min-w-0">
        <span>{t('timedFees.generalLevel')}</span>
        <select
          className="w-full border border-base-300 bg-base-100 px-3 py-2 text-base-content focus:outline-2 focus:outline-primary disabled:opacity-60"
          value={draft.generalMemberLevelId}
          onChange={(event) =>
            onChange({
              ...draft,
              generalMemberLevelId: event.target.value,
              memberVenuePrices: draft.memberVenuePrices.filter(
                (row) => row.memberLevelId !== event.target.value,
              ),
            })
          }
        >
          <option value="">{t('timedFees.selectGeneral')}</option>
          {levels.map((level) => (
            <option key={level.id} value={level.id}>
              {level.displayName}
            </option>
          ))}
        </select>
      </label>
      <div className="grid gap-3 md:grid-cols-2">
        {field('originalVenueFeeAmount', t('timedFees.original'))}
        {field('generalReservationVenueFeeAmount', t('timedFees.generalReservation'))}
        {field('afterBreakVenueFeeAmount', t('timedFees.afterBreak'))}
      </div>
      {levels
        .filter((level) => level.id !== draft.generalMemberLevelId)
        .map((level) => {
          const row = draft.memberVenuePrices.find((row) => row.memberLevelId === level.id);
          return (
            <div key={level.id} className="grid gap-3 md:grid-cols-2">
              {(['ordinaryAmount', 'reservationAmount'] as const).map((key) => (
                <label className="flex flex-col gap-1 min-w-0" key={key}>
                  <span>
                    {level.displayName} · {t(`timedFees.${key}`)}
                  </span>
                  <input
                    className="w-full border border-base-300 bg-base-100 px-3 py-2 text-base-content focus:outline-2 focus:outline-primary disabled:opacity-60"
                    type="text"
                    inputMode="decimal"
                    value={row?.[key] ?? ''}
                    onChange={(event) =>
                      onChange({
                        ...draft,
                        principalAmount: principal,
                        memberVenuePrices: [
                          ...draft.memberVenuePrices.filter(
                            (item) => item.memberLevelId !== level.id,
                          ),
                          {
                            memberLevelId: level.id,
                            ordinaryAmount: row?.ordinaryAmount ?? '',
                            reservationAmount: row?.reservationAmount ?? '',
                            [key]: event.target.value,
                          },
                        ],
                      })
                    }
                  />
                </label>
              ))}
            </div>
          );
        })}
      <div className="grid gap-3 md:grid-cols-3">
        {(['reentry', 'rebuy', 'addon'] as const).map((key) => (
          <label className="flex flex-col gap-1 min-w-0" key={key}>
            <span>{t(`timedFees.${key}`)}</span>
            <input
              className="w-full border border-base-300 bg-base-100 px-3 py-2 text-base-content focus:outline-2 focus:outline-primary disabled:opacity-60"
              type="text"
              inputMode="decimal"
              required
              value={draft.repeatVenueFees[key]}
              onChange={(event) =>
                onChange({
                  ...draft,
                  principalAmount: principal,
                  repeatVenueFees: { ...draft.repeatVenueFees, [key]: event.target.value },
                })
              }
            />
            <span className="text-xs">{t('timedFees.samePrincipal', { amount: principal })}</span>
          </label>
        ))}
      </div>
      <p>{t('timedFees.explicitRepeat')}</p>
      <p className="text-sm">{t('timedFees.saveVersion')}</p>
    </fieldset>
  );
}
